import { useMemo } from 'react';
import React from 'react';
import { useIsFocused } from '@react-navigation/native';
import { useLayoutSize } from '../../theme';
import WebHeaderBar, { type WebHeaderBarProps } from './WebHeaderBar';
import useWebHeader from './useWebHeader';

/**
 * Renders a WebHeaderBar and injects it into the WebShellContext header slot.
 * Must be rendered inside a WebShellProvider (e.g. within WebSidebarLayout
 * or BackOfficeSidebarLayout). Returns null — the header is rendered by the
 * shell, not inline.
 *
 * When the sidebar layout is not active, or this screen is not focused, does
 * nothing. Focus gating prevents stale headers when multiple mounted tabs share
 * one WebShellProvider (e.g. hoisted WebSidebarLayout).
 */
export default function WebHeaderBarInjector(props: WebHeaderBarProps) {
  const { useSidebarNav } = useLayoutSize();
  const isFocused = useIsFocused();
  const active = useSidebarNav && isFocused;

  const headerNode = useMemo(
    () =>
      active ? (
        <WebHeaderBar
          onBack={props.onBack}
          title={props.title}
          titleAlign={props.titleAlign}
          rightSlot={props.rightSlot}
          showBack={props.showBack}>
          {props.children}
        </WebHeaderBar>
      ) : null,
    [
      active,
      props.onBack,
      props.title,
      props.titleAlign,
      props.rightSlot,
      props.showBack,
      props.children,
    ],
  );

  useWebHeader(headerNode, active);

  return null;
}
